import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AuthAwareLink from '../components/AuthAwareLink';
import Seo from '../seo/Seo';
import CollapsibleSeoContent from '../components/seo/CollapsibleSeoContent';
import { getSeoContent, getSeoFaqSchema } from '../seo/seoContentData';

const sitemapGroups = [
  {
    heading: 'Main Pages',
    description:
      'Core MyRoomsAI pages including the home overview, product features, pricing, FAQ, and real before-and-after transformation gallery.',
    links: [
      {
        label: 'Home',
        path: '/',
        description:
          'All-in-one AI home design platform for interior, exterior, garden, and floorplan visualization.',
      },
      {
        label: 'Features',
        spaPath: '/features',
        staticPath: '/features/',
        description: 'Overview of MyRoomsAI capabilities, design workflows, and platform benefits.',
      },
      {
        label: 'Real Transformations',
        path: '/real-transformations',
        description: 'Before-and-after AI room, kitchen, exterior, and garden transformation examples.',
      },
      {
        label: 'FAQ',
        spaPath: '/faq',
        staticPath: '/faq/',
        description: 'Answers about credits, design styles, uploads, generation time, and getting started.',
      },
      {
        label: 'Pricing',
        path: '/pricing',
        description: 'Credit packs and pricing for AI room design, floorplan, and renovation tools.',
      },
    ],
  },
  {
    heading: 'AI Tools',
    description:
      'Public AI design tools for room decoration, furniture replacement, exterior and garden design, and floorplan visualization workflows.',
    links: [
      {
        label: 'AI Room Decoration Tool',
        path: '/ai-tools/ai-room-decoration',
        description: 'Redesign room interiors from a photo with new furniture, colors, and decoration styles.',
      },
      {
        label: 'AI Room Furniture Replacement Tool',
        path: '/ai-tools/ai-furniture-replacement',
        description: 'Preview new furniture layouts in your existing room before buying or moving pieces.',
      },
      {
        label: 'AI 3D Model to Realistic Interior Image',
        path: '/ai-tools/ai-3d-model-to-realistic-interior',
        description:
          'Convert 3D interior model screenshots from Blender, SketchUp, or Three.js into photorealistic interior images.',
      },
      {
        label: 'AI Exterior Home Design Tool',
        path: '/ai-tools/ai-exterior-design',
        description: 'Visualize facade upgrades, materials, and curb-appeal changes on your home exterior.',
      },
      {
        label: 'AI Garden Design Tool',
        path: '/ai-tools/ai-garden-design',
        description: 'Explore landscaping and outdoor living ideas from a real yard or patio photo.',
      },
      {
        label: 'AI 2D Floor Plan to 3D Transformer',
        path: '/ai-tools/ai-2d-floorplan-to-3d',
        description: 'Convert a 2D floor plan image into an open-top 3D-style home preview.',
      },
      {
        label: 'AI Floor Plan to Realistic Room Generator',
        path: '/ai-tools/ai-2d-floorplan-to-room-render',
        description: 'Turn a selected floor plan room into a realistic interior render.',
      },
      {
        label: 'AI Floor Plan Creator',
        path: '/ai-tools/ai-2d-floorplan-creator',
        description: 'Describe your layout needs and explore AI-generated 2D floor plan concepts.',
      },
      {
        label: 'AI 2D Floor Plan Designer',
        path: '/ai-tools/ai-2d-floorplan-designer',
        description: 'Draw walls, rooms, doors, and windows manually in a browser-based 2D floor plan editor.',
      },
    ],
  },
  {
    heading: 'Room Types',
    description:
      'Room-type guides with tips for AI bedroom, living room, kitchen, and office design from your photos.',
    links: [
      {
        label: 'Living Room AI Design',
        path: '/room-types/living-room',
        description: 'AI living room design ideas for layouts, seating, and interior style direction.',
      },
      {
        label: 'Bedroom AI Design',
        path: '/room-types/bedroom',
        description: 'Bedroom design by AI with layout, color, and furniture preview workflows.',
      },
      {
        label: 'Kitchen AI Design',
        path: '/room-types/kitchen',
        description: 'AI kitchen design from photo for cabinets, finishes, and renovation planning.',
      },
      {
        label: 'Office AI Design',
        path: '/room-types/office',
        description: 'Home office and workspace layout previews for desks, storage, and focus-friendly setups.',
      },
    ],
  },
  {
    heading: 'Gallery',
    description:
      'Curated design galleries showcasing AI living room, bedroom, and kitchen inspiration collections.',
    links: [
      {
        label: 'All galleries',
        path: '/gallery/',
        description: 'Browse all MyRoomsAI design gallery collections in one place.',
      },
      {
        label: 'AI Living Room Design Gallery',
        path: '/gallery/ai-living-room-gallery/',
        description: 'Living room interior inspiration and AI design examples.',
      },
      {
        label: 'AI Bedroom Design Gallery',
        path: '/gallery/ai-bedroom-gallery/',
        description: 'Bedroom styling ideas and AI-generated bedroom design samples.',
      },
      {
        label: 'AI Kitchen Design Gallery',
        path: '/gallery/ai-kitchen-gallery/',
        description: 'Kitchen renovation and cabinetry inspiration from AI design previews.',
      },
    ],
  },
  {
    heading: 'Blog',
    description:
      'Articles on AI room design basics, affordable interior AI, living room styling, and floorplan-to-room workflows.',
    links: [
      {
        label: 'Blog Home',
        path: '/blog/',
        description: 'Latest articles on AI interior design, room styling, and floorplan workflows.',
      },
      {
        label: 'AI Room Designer',
        path: '/blog/ai-room-designer/',
        description: 'Introduction to using an AI room designer for home decoration projects.',
      },
      {
        label: 'AI Interior Design Basics',
        path: '/blog/ai-interior-design-basics/',
        description: 'Foundational guide to AI interior design tools, uploads, and best results.',
      },
      {
        label: 'AI Bedroom Design Ideas',
        path: '/blog/ai-bedroom-design-ideas/',
        description: 'Bedroom styling tips and AI-generated design inspiration.',
      },
      {
        label: 'AI Room Designer for Living Room',
        path: '/blog/ai-room-designer-living-room/',
        description: 'Living room layout and decor ideas using AI room design tools.',
      },
      {
        label: 'Affordable AI Interior Designer',
        path: '/blog/affordable-ai-interior-designer/',
        description: 'How to plan room redesigns on a budget with AI visualization.',
      },
      {
        label: '2D FloorPlan to AI Bedroom Image',
        path: '/blog/2d-floorplan-to-ai-bedroom-image/',
        description: 'Workflow for turning a floor plan into a realistic bedroom preview.',
      },
    ],
  },
  {
    heading: 'Company',
    description: 'Legal and site navigation pages for MyRoomsAI.',
    links: [
      {
        label: 'Sitemap',
        path: '/sitemap',
        description: 'This page — a structured index of public MyRoomsAI pages and tools.',
      },
      {
        label: 'Terms & Conditions',
        path: '/terms-and-conditions',
        description: 'Terms of service, usage policies, and legal information for MyRoomsAI.',
      },
    ],
  },
];

function renderSitemapLink({ label, path, spaPath, staticPath }) {
  if (spaPath && staticPath) {
    return (
      <AuthAwareLink spaPath={spaPath} staticPath={staticPath}>
        {label}
      </AuthAwareLink>
    );
  }
  if (path.startsWith('/#') || path.startsWith('/blog/') || path.startsWith('/gallery/')) {
    return <a href={path}>{label}</a>;
  }
  return <Link to={path}>{label}</Link>;
}

function SitemapPage() {
  return (
    <div className="page landing-bg">
      <Seo
        title="MyRoomsAI Sitemap & Website Pages | MyRoomsAI"
        description="Browse all public pages on MyRoomsAI — AI design tools, room type guides, galleries, blog articles, pricing, and support pages."
        canonicalPath="/sitemap"
        keywords="MyRoomsAI sitemap, all pages, site index, AI design tools"
        structuredData={[getSeoFaqSchema('/sitemap')].filter(Boolean)}
      />
      <Navbar />
      <main className="container section sitemap-page">
        <h1>Site Map</h1>
        <p>
          Browse every major public section of MyRoomsAI — from AI room decoration and floorplan tools to room-type
          guides, galleries, blog articles, and company pages. Each link below includes a short description to help
          you find the right page quickly.
        </p>

        <div className="sitemap-groups">
          {sitemapGroups.map(({ heading, description, links }) => (
            <section key={heading} className="sitemap-group">
              <h2>{heading}</h2>
              {description ? <p className="seo-content-text">{description}</p> : null}
              <ul className="sitemap-list">
                {links.map((entry) => (
                  <li key={entry.path || entry.staticPath} className="sitemap-list-item">
                    <div className="sitemap-link-block">
                      {renderSitemapLink(entry)}
                      {entry.description ? (
                        <p className="seo-content-text sitemap-link-desc">{entry.description}</p>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </main>
      <CollapsibleSeoContent {...getSeoContent('/sitemap')} />
      <Footer />
    </div>
  );
}

export default SitemapPage;
